'use client';

import Image from 'next/image';
import { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { CarImage } from '@/slw/entities/seller/listing';
import { cn } from '@/slw/shared/lib/utils';

interface PreviewImageProps {
  src: string | undefined;
  alt: string;
  isRemoteImage: boolean | undefined;
  onError: () => void;
  onNext: () => void;
  onPrevious: () => void;
  showNavigation: boolean;
}

function PreviewImage({ src, alt, isRemoteImage, onError, onNext, onPrevious, showNavigation }: PreviewImageProps) {
  const [isLoading, setLoading] = useState(true);

  return (
    <div className='relative'>
      <Image
        src={src || ''}
        alt={alt}
        className={cn(
          'rounded-md transition-all duration-200 object-cover w-[899px] h-[427px]',
          isLoading ? 'grayscale blur-xs' : 'grayscale-0 blur-0',
        )}
        width={899}
        height={427}
        loading='lazy'
        onError={onError}
        unoptimized={!isRemoteImage}
        onLoadingComplete={() => setLoading(false)}
      />
      {showNavigation && (
        <>
          <button
            type='button'
            onClick={(e) => {
              e.stopPropagation();
              onPrevious();
            }}
            className='absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white 
            rounded-full p-2 shadow-md transition-all duration-200'
            aria-label='Previous image'
          >
            <ChevronLeft size={24} className='text-[#3A3838]' />
          </button>
          <button
            type='button'
            onClick={(e) => {
              e.stopPropagation();
              onNext();
            }}
            className='absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white 
            rounded-full p-2 shadow-md transition-all duration-200'
            aria-label='Next image'
          >
            <ChevronRight size={24} className='w-6 h-6 text-[#3A3838]' />
          </button>
        </>
      )}
    </div>
  );
}

interface ThumbnailListProps {
  images: CarImage[];
  selectedIdx: number | null;
  onSelectImage: (index: number) => void;
}

interface ThumbnailButtonProps {
  imageUrl: string;
  index: number;
  isSelected: boolean;
  onSelect: (index: number) => void;
}

function ThumbnailButton({ imageUrl, index, isSelected, onSelect }: ThumbnailButtonProps) {
  const normalizeImageUrl = (url: string | undefined): string => {
    if (!url) return '/images/common/no-image-available.jpg';
    
    // Fix malformed URLs like "https:/file.c10depot.com" -> "https://file.c10depot.com"
    if (url.startsWith('https:/') && !url.startsWith('https://')) {
      url = url.replace('https:/', 'https://');
    }
    if (url.startsWith('http:/') && !url.startsWith('http://')) {
      url = url.replace('http:/', 'http://');
    }
    
    if (url.startsWith('http')) {
      return url;
    }

    return '/images/common/no-image-available.jpg';
  };
  const [isLoading, setLoading] = useState(true);

  const initialSrc = useMemo(() => normalizeImageUrl(imageUrl), [imageUrl]);

  const [previewImageSrc, setPreviewImageSrc] = useState(initialSrc);

  const isRemoteImage = previewImageSrc?.startsWith('http');

  const handleImageError = () => {
    setPreviewImageSrc('/images/common/no-image-available.png');
  };

  return (
    <button
      type='button'
      tabIndex={0}
      aria-label={`Show image ${index + 1}`}
      onClick={() => onSelect(index)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onSelect(index);
      }}
      className={cn(
        'w-fit h-auto rounded-md overflow-hidden border-2 transition-all duration-150 snap-center flex-shrink-0',
        isSelected ? 'border-[#B6864C] ring-2 ring-[#B6864C]' : 'border-transparent hover:border-[#B6864C]/50',
      )}
      style={{ outline: 'none' }}
    >
      <Image
        src={previewImageSrc ?? ''}
        alt={previewImageSrc ?? ''}
        className={cn(
          'rounded-md transition-all duration-200 object-cover w-[206px] h-[97px]',
          isLoading ? 'grayscale blur-xs' : 'grayscale-0 blur-0',
        )}
        loading='lazy'
        width={206}
        height={97}
        onError={handleImageError}
        unoptimized={!isRemoteImage}
        onLoadingComplete={() => setLoading(false)}
      />
    </button>
  );
}

function ThumbnailList({ images, selectedIdx, onSelectImage }: ThumbnailListProps) {
  if (images.length <= 1) {
    return null;
  }

  return (
    <div className='flex gap-4 overflow-x-auto snap-x snap-mandatory pb-4 mt-4 max-w-[900px]'>
      {images.map((img, idx) => {
        // Use S3 URL directly from imageUrl, no fallback needed
        const imageUrl = img.imageUrl || undefined;
        return (
        <ThumbnailButton
          key={idx}
            imageUrl={imageUrl}
          index={idx}
          isSelected={selectedIdx === idx}
          onSelect={onSelectImage}
        />
        );
      })}
    </div>
  );
}

interface ListingGalleryProps {
  images: CarImage[];
  mainImageId?: string;
  mainImageUrl?: string;
}

export function ListingGallery({ images, mainImageId, mainImageUrl }: ListingGalleryProps) {
  const defaultIndex = useMemo(() => {
    if (!mainImageId || images.length === 0) return null;

    return images.findIndex((img) => img.imageId === mainImageId);
  }, [images, mainImageId]);

  useEffect(() => {
    setSelectedIdx(defaultIndex);
  }, [defaultIndex]);

  const [selectedIdx, setSelectedIdx] = useState<number | null>(defaultIndex);

  const normalizeImageUrl = (url: string | undefined): string => {
    if (!url) return '/images/common/no-image-available.jpg';
    
    // Fix malformed URLs like "https:/file.c10depot.com" -> "https://file.c10depot.com"
    if (url.startsWith('https:/') && !url.startsWith('https://')) {
      url = url.replace('https:/', 'https://');
    }
    if (url.startsWith('http:/') && !url.startsWith('http://')) {
      url = url.replace('http:/', 'http://');
    }
    
    if (url.startsWith('http')) {
      return url;
    }

    return '/images/common/no-image-available.jpg';
  };

  // Find main image URL: priority: mainImageUrl prop > imageUrl from images array
  const mainImage = images.find((img) => img.imageId === mainImageId);
  const mainImageSrc = mainImageUrl || mainImage?.imageUrl || undefined;
  const initialSrc = useMemo(() => normalizeImageUrl(mainImageSrc), [mainImageSrc]);
  const [previewImageSrc, setPreviewImageSrc] = useState(initialSrc);

  const isRemoteImage = previewImageSrc?.startsWith('http');

  const handleImageError = () => {
    setPreviewImageSrc('/images/common/no-image-available.png');
  };

  const handleNextImage = () => {
    if (images.length <= 1) return;

    const nextIdx = selectedIdx === null ? 0 : selectedIdx === images.length - 1 ? 0 : selectedIdx + 1;

    setSelectedIdx(nextIdx);
  };

  const handlePreviousImage = () => {
    if (images.length <= 1) return;

    const prevIdx = selectedIdx === null ? images.length - 1 : selectedIdx === 0 ? images.length - 1 : selectedIdx - 1;

    setSelectedIdx(prevIdx);
  };

  useEffect(() => {
    if (selectedIdx !== null && images[selectedIdx]) {
      const selectedImage = images[selectedIdx];
      // Use S3 URL directly from imageUrl
      const imageUrl = selectedImage.imageUrl || undefined;
      setPreviewImageSrc(normalizeImageUrl(imageUrl));
    } else {
      setPreviewImageSrc(initialSrc);
    }
  }, [images, selectedIdx, initialSrc]);

  return (
    <div>
      <div>
        <PreviewImage
          src={previewImageSrc}
          alt={previewImageSrc ?? ''}
          isRemoteImage={isRemoteImage}
          onError={handleImageError}
          onNext={handleNextImage}
          onPrevious={handlePreviousImage}
          showNavigation={images.length > 1}
        />
      </div>
      <ThumbnailList images={images} selectedIdx={selectedIdx} onSelectImage={setSelectedIdx} />
    </div>
  );
}
